import React from "react";
import axios from "axios";
import { Circle, CircleMarker, Marker, Polyline, Popup, Tooltip } from "react-leaflet";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "../../redux/hooks";
import { positionSelectedIcon } from "../../utils/mapIcons";
import { apiKey, apiPrefix } from "../../config";
import { getLineNumberShort, shapeArrayOperations, vehicleNameOrId } from "../../utils/helpers";
import { AgencyContext } from "../../hooks/AgencyContext";

const SingleReportAVLMap = (): JSX.Element => {
  const { t } = useTranslation();

  //* AgencyContext
  const { agencyId } = React.useContext(AgencyContext) as AgencyContextType;

  const [historicalPositions, lineNumberFromRedux, positionTimeFromRedux, routeStopsBoth]: [
    Array<VehicleReportAVL>,
    string,
    string,
    string
  ] = useAppSelector((state: RootState) => [
    state?.historicalPositions.historicalPositions,
    state?.historicalPositions?.busId_LineNumber?.lineNumber,
    state?.historicalPositions?.positionTime,
    state?.historicalPositions?.routeStopsBoth,
  ]);
  // console.log({ routeStopsBoth });

  const [positionTime, setPositionTime] = React.useState<string>("");
  const [dataReportAVL, setDataReportAVL] = React.useState<Array<VehicleReportAVL> | null>();
  const [lineShapeState, setLineShapeState] = React.useState<Array<Line> | null>(null);
  const [stops_0, setStops_0] = React.useState<Array<BusStop> | null>(null);
  const [stops_1, setStops_1] = React.useState<Array<BusStop> | null>(null);
  // console.log("stops_0:", stops_0);

  React.useEffect(() => {
    if (positionTimeFromRedux) {
      setPositionTime(positionTimeFromRedux);
    }
  }, [positionTimeFromRedux]);

  React.useEffect(() => {
    if (historicalPositions) {
      setDataReportAVL(historicalPositions);
    }
  }, [historicalPositions]);

  const addDirection = (stopArray: BusStop[], direction: string) => {
    for (let i = 0; i < stopArray?.length; i++) {
      stopArray[i].direction = direction;
    }
    return stopArray;
  };

  React.useEffect(() => {
    if (lineNumberFromRedux) {
      const lineNumberFromReduxShort = getLineNumberShort(lineNumberFromRedux);
      // console.log({ lineNumberFromReduxShort });
      axios
        .get(`${apiPrefix}/v1/key/${apiKey}/agency/${agencyId}/command/routesDetails?r=${lineNumberFromReduxShort}`)
        .then(({ data }) => {
          // console.log("data:", data);
          const lineShapeData = data?.routes[0]?.shape;
          // console.log("lineShapeData:", lineShapeData);
          const flatArrayTwoDirections = shapeArrayOperations(lineShapeData);
          // console.log("flatArrayDirection:", flatArrayDirection);
          setLineShapeState(flatArrayTwoDirections);
          const stops = data?.routes[0]?.direction;
          // console.log("stops:", stops);
          const stopPointsObject_0 = stops.filter((stopSet: { id: string }) => stopSet.id === "0");
          // console.log("stopPointsObject_0", stopPointsObject_0);
          const stopPoints_0 = stopPointsObject_0[0]?.stop as BusStop[];
          const stopPointsObject_1 = stops.filter((stopSet: { id: string }) => stopSet.id === "1");
          // console.log("stopPointsObject_1", stopPointsObject_1);
          const stopPoints_1 = stopPointsObject_1[0]?.stop as BusStop[];

          const stopPoints_0_Direction = addDirection(stopPoints_0, "0");
          const stopPoints_1_Direction = addDirection(stopPoints_1, "1");
          setStops_0(stopPoints_0_Direction);
          setStops_1(stopPoints_1_Direction);
        })
        .catch((error) => {
          console.log("error:", error);
        });
    }
  }, [agencyId, lineNumberFromRedux]);

  const circleOptions = { color: "Blue", weight: 2, fillColor: "Blue", fillOpacity: 1 };
  const circleOptionsSelected = { color: "DarkOrange", weight: 2, fillColor: "DarkOrange", fillOpacity: 0.5 };

  const InfoPositionDiv = ({ elem }: { elem: VehicleReportAVL }): JSX.Element => (
    <div>
      {t("Id pojazdu")}: <span className="span_bold">{vehicleNameOrId(elem)}</span>
      <br />
      {t("Czas")}: <span className="span_bold">{elem.time}</span>
      <br />
      {t("Pozycja")}:{" "}
      <span className="span_bold">
        {elem.lat}, {elem.lon}
      </span>
      <br />
      {t("Źródło")}: <span className="span_bold">{elem.source}</span>
      <br />
    </div>
  );

  const historicalPositionsPath =
    dataReportAVL &&
    (dataReportAVL.map((elem: VehicleReportAVL, index: number) => (
      <Circle
        key={String(elem.lat + " " + elem.lon + " " + index)}
        center={{
          lat: elem.lat,
          lng: elem.lon,
        }}
        radius={positionTime === elem.time ? 4 : 2}
        pathOptions={positionTime === elem.time ? circleOptionsSelected : circleOptions}
      >
        <Popup>
          <InfoPositionDiv elem={elem} />
        </Popup>
        {positionTimeFromRedux && positionTime === elem.time ? (
          <Marker
            icon={positionSelectedIcon}
            key={String(elem.lat + " " + elem.lon + " " + index)}
            position={{
              lat: elem.lat,
              lng: elem.lon,
            }}
          >
            <Tooltip permanent={true} direction={"top"}>
              <InfoPositionDiv elem={elem} />
            </Tooltip>
          </Marker>
        ) : null}
      </Circle>
    )) as JSX.Element[]);

  const directionOptions = { color: "DarkCyan", weight: 4 };
  const directionOptionsLight = { color: "Green", weight: 3 };

  const PathShapeTwoDirections = () =>
    lineShapeState &&
    (lineShapeState.map((elem, index) => {
      return (
        <Polyline
          key={index}
          pathOptions={index === lineShapeState?.length - 1 ? directionOptions : directionOptionsLight}
          positions={elem}
        />
      );
    }) as JSX.Element[]);

  const stops_0_Options = { fillColor: "BlueViolet", color: "BlueViolet", fillOpacity: 0.5 };
  const stops_1_Options = { fillColor: "Peru", color: "Peru", fillOpacity: 0.5 };

  const busStops_0 =
    stops_0 &&
    (stops_0.map((elem: BusStop, index: number) => (
      <CircleMarker
        pathOptions={stops_0_Options}
        key={String(elem.lat + " " + elem.lon + " " + index)}
        center={{
          lat: elem.lat,
          lng: elem.lon,
        }}
        radius={6}
      >
        <Popup>
          <div>
            {t("Nazwa")} <span className="span_bold">{elem.name}</span>
            <br />
            {t("Id")} <span className="span_bold">{elem.id}</span>
            <br />
            {t("Kierunek")}: <span className="span_bold">{elem.direction}</span>
          </div>
        </Popup>
      </CircleMarker>
    )) as JSX.Element[]);

  const busStops_1 =
    stops_1 &&
    (stops_1.map((elem: BusStop, index: number) => (
      <CircleMarker
        pathOptions={stops_1_Options}
        key={String(elem.lat + " " + elem.lon + " " + index)}
        center={{
          lat: elem.lat,
          lng: elem.lon,
        }}
        radius={6}
      >
        <Popup>
          <div>
            {t("Nazwa")} <span className="span_bold">{elem.name}</span>
            <br />
            {t("Id")} <span className="span_bold">{elem.id}</span>
            <br />
            {t("Kierunek")}: <span className="span_bold">{elem.direction}</span>
          </div>
        </Popup>
      </CircleMarker>
    )) as JSX.Element[]);

  return (
    <React.Fragment>
      {dataReportAVL && historicalPositionsPath}
      {lineShapeState &&
        routeStopsBoth &&
        (routeStopsBoth === "both" || routeStopsBoth === "route") &&
        PathShapeTwoDirections()}
      {routeStopsBoth && (routeStopsBoth === "both" || routeStopsBoth === "stops") && (
        <React.Fragment>
          {busStops_0}
          {busStops_1}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default SingleReportAVLMap;
